import { type ResponseMESF, useUTLSettingsContext } from "@dexteel/mesf-core";
import { Box, Paper } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useState } from "react";
import { CenteredLazyLoading } from "../../../../../../controls/LazyLoading";
import { AgGridFooterActions } from "../../../../../../shared/data-entries/AgGridFooterActions";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTimerContext } from "../../../context/timer.provider";
import { useLMFDataEntryActions } from "../../../hooks/useLMFDataEntryActions";
import {
  readAdditionsFromPLC,
  readWireAdditionsFromPLC,
} from "../../../repositories/lmf.repositories";
import { AdditionModal } from "../../modals/addition/AdditionModal";
import { useTheme } from "@mui/material/styles";
import { lmfDETablesStyles } from "../styles/lmfDETablesStyles";
import { useTableData } from "./hooks/useAdditionsDataTable";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

export const TableAdditions = () => {
  const theme = useTheme();
  const styles = lmfDETablesStyles(theme);

  const { pause, resume } = useTimerContext();

  const {
    state: { deProcess },
    actions: { setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const { deleteAddition, refreshData } = useLMFDataEntryActions();

  const [showModal, setShowModal] = useState<"NEW" | "EDIT" | "">("");
  const [selectedAddition, setSelectedAddition] = useState<any>();

  const additions = deProcess?.additions;

  const readAutomaticAdditions = async (value?: string) => {
    if (value === "OK") {
      const resp = await readAdditionsFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
      });
      const wireResp = await readWireAdditionsFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
      });
      if (resp.ok && wireResp.ok) {
        setNotificationMessage("Read additions from PLC successful");
        await refreshData();
        return resp as ResponseMESF;
      } else {
        const message =
          (!resp.ok && resp.message) ||
          (!wireResp.ok && wireResp.message) ||
          "Read additions from PLC failed";

        setErrorMessage(message);
        return {
          ok: false,
          message,
        } as ResponseMESF;
      }
    } else {
      return {} as ResponseMESF;
    }
  };

  const editAddition = (AdditionId: number) => {
    pause();
    const addition = additions?.find((a) => a.AdditionId === AdditionId);
    setSelectedAddition(addition);
    setShowModal("EDIT");
  };

  const { columns } = useTableData({
    deleteAddition,
    editAddition,
  });

  return (
    <>
      <Paper>
        <Box padding={1} sx={styles.agGridBox}>
          <AgGridReact
            rowData={additions}
            columnDefs={columns}
            defaultColDef={{ ...defaultColDef, sortable: false }}
            loadingOverlayComponent={CenteredLazyLoading}
            {...defaultGridOptions}
          />
          <AgGridFooterActions
            setShowModal={setShowModal}
            readFromPLC={readAutomaticAdditions}
            table="additions"
            pause={pause}
          />
        </Box>
      </Paper>
      <AdditionModal
        showModal={showModal}
        addition={selectedAddition}
        onHide={() => {
          resume();
          setShowModal("");
        }}
      />
    </>
  );
};
